import {
  Button,
  ButtonProps,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  StackDivider,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { SimpleCard } from 'components/cards/SimpleCard';
import { CenteredSpinner } from 'components/CenteredSpinner';
import { InputDialog } from 'components/dialogs/InputDialog';
import { ErrorMessage } from 'components/ErrorMessage';
import {
  CreateFolderIcon,
  FolderOpenIcon,
  NavigateBackIcon,
} from 'components/icons';
import { OverlayProps } from 'components/TaskOverlay';
import { orderBy } from 'natural-orderby';
import React, { Suspense, useState } from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useMutation, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { MoveFolderNodeModal_createFolder_Mutation } from './__generated__/MoveFolderNodeModal_createFolder_Mutation.graphql';
import {
  MoveFolderNodeModal_folder_Query,
  MoveFolderNodeModal_folder_Query$data,
} from './__generated__/MoveFolderNodeModal_folder_Query.graphql';
import { MoveFolderNodeModal_moveFolderNodes_Mutation } from './__generated__/MoveFolderNodeModal_moveFolderNodes_Mutation.graphql';
import { useFeatureFlag } from 'utils/sessionProvider';

const folderQuery = graphql`
  query MoveFolderNodeModal_folder_Query($folderId: ID!) {
    folder: node(id: $folderId) {
      id
      ... on Folder {
        name
        canCreateFolderIn {
          value
        }
        canMoveInto {
          value
        }
        parent: ancestors(first: 1) {
          edges {
            node {
              ... on Folder {
                id
              }
            }
          }
        }
        children {
          __id
          edges {
            node {
              ... on FolderItem {
                __typename
                id
              }
              ... on Folder {
                __typename
                id
                name
                canCreateFolderIn {
                  value
                }
                canMoveInto {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

const createFolderMutation = graphql`
  mutation MoveFolderNodeModal_createFolder_Mutation(
    $name: String!
    $parentFolderId: ID!
    $copyParentPermissions: Boolean
    $connections: [ID!]!
  ) {
    createFolder(
      input: {
        name: $name
        parentFolderId: $parentFolderId
        copyParentPermissions: $copyParentPermissions
      }
    ) {
      folderCreated
        @appendNode(connections: $connections, edgeTypeName: "FolderNodeEdge") {
        id
        name
        canCreateFolderIn {
          value
        }
        canMoveInto {
          value
        }
        ...ItemMenu_node
      }
    }
  }
`;

const moveFolderNodeMutation = graphql`
  mutation MoveFolderNodeModal_moveFolderNodes_Mutation(
    $folderNodeIds: [ID!]!
    $targetFolderId: ID!
    $originConnections: [ID!]!
  ) {
    moveFolderNodes(
      input: { folderNodeIds: $folderNodeIds, targetFolderId: $targetFolderId }
    ) {
      folderNodesMovedConnection {
        edges {
          node {
            ... on FolderItem {
              id @deleteEdge(connections: $originConnections)
            }
            ... on Folder {
              id @deleteEdge(connections: $originConnections)
            }
          }
        }
      }
      targetFolder {
        ...Folder_folder
      }
    }
  }
`;

interface MoveFolderNodeModalProps extends OverlayProps {
  rootFolderId: string;
  nodesBeingMovedIds: string[];
  parentConnectionId?: string;
}

type ChildrenTypes = NonNullable<
  NonNullable<
    NonNullable<MoveFolderNodeModal_folder_Query$data['folder']>['children']
  >['edges'][number]['node']
>;

type FolderType = Extract<ChildrenTypes, { __typename: 'Folder' }>;

export const MoveFolderNodeModal = (props: MoveFolderNodeModalProps) => {
  const {
    rootFolderId,
    nodesBeingMovedIds,
    parentConnectionId,
    ...modalProps
  } = props;
  return (
    <Modal {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        <ErrorBoundary
          fallback={
            <ModalBody>
              <ErrorFallback />
            </ModalBody>
          }
        >
          <Suspense fallback={<CenteredSpinner />}>
            <UnsuspendedMoveFolderNodeModal {...props} />
          </Suspense>
        </ErrorBoundary>
      </ModalContent>
    </Modal>
  );
};

const UnsuspendedMoveFolderNodeModal = ({
  rootFolderId,
  nodesBeingMovedIds,
  parentConnectionId,
  onClose,
}: MoveFolderNodeModalProps) => {
  const isFoldersCopyModulePermissionsEnabled = useFeatureFlag(
    'FEATURE_FOLDERS_COPY_MODULE_PERMISSIONS',
  );

  const { useLazyLoadQuery } = useSafeRelay();
  const [currentFolderId, setCurrentFolderId] = useState(rootFolderId);
  const { folder } = useLazyLoadQuery<MoveFolderNodeModal_folder_Query>(
    folderQuery,
    {
      folderId: currentFolderId,
    },
  );

  const {
    isOpen: isCreateFolderDialogOpen,
    onClose: onCloseCreateFolderDialog,
    onOpen: onOpenCreateFolderDialog,
  } = useDisclosure();

  const [createFolder, isCreateFolderLoading] =
    useMutation<MoveFolderNodeModal_createFolder_Mutation>(
      createFolderMutation,
    );

  const [moveFolderNode, isMoveLoading] =
    useMutation<MoveFolderNodeModal_moveFolderNodes_Mutation>(
      moveFolderNodeMutation,
    );

  const toast = useToast();
  const headerBg = useColorModeValue('gray.50', 'gray.600');

  if (!folder) {
    throw new Error('Unknown folder');
  }

  const { name, children, parent: _parent } = folder;
  const parentFolder =
    _parent?.edges && _parent.edges.length > 0 && _parent.edges[0]?.node;
  const parentFolderId = parentFolder && parentFolder.id;

  const subFolders =
    children?.edges
      .filter(notEmpty)
      .map(({ node }) => node)
      .filter((n): n is FolderType => {
        return notEmpty(n) && n.__typename === 'Folder';
      }) || ([] as FolderType[]);

  const orderedSubFolders = orderBy(subFolders, (item) => item.name);

  const onCreateFolder = (name: string) => {
    createFolder({
      variables: {
        name,
        parentFolderId: folder.id,
        copyParentPermissions: isFoldersCopyModulePermissionsEnabled,
        connections: folder.children ? [folder.children.__id] : [],
      },
      onCompleted: onCloseCreateFolderDialog,
    });
  };

  const isMoveWithinItself = folder.children?.edges.some(({ node }) => {
    if (
      !node ||
      (node?.__typename !== 'Folder' && node?.__typename !== 'FolderItem')
    ) {
      return false;
    }

    const firstNodeBeingMovedId = nodesBeingMovedIds[0]; // We only need to check the first node, since all nodes are being moved from the same folder
    return node?.id === firstNodeBeingMovedId;
  });

  const onMove = () => {
    moveFolderNode({
      variables: {
        folderNodeIds: nodesBeingMovedIds,
        targetFolderId: folder.id,
        originConnections: parentConnectionId ? [parentConnectionId] : [],
      },
      onCompleted: () => {
        toast({
          title: t`Item moved`,
          status: 'success',
          isClosable: true,
        });
        onClose();
      },
    });
  };

  return (
    <>
      <InputDialog
        title={t`Name your folder`}
        submitButtonLabel={t`Create`}
        isOpen={isCreateFolderDialogOpen}
        onClose={onCloseCreateFolderDialog}
        onSubmitInput={onCreateFolder}
        isLoading={isCreateFolderLoading}
        placeholder={t`Folder`}
        validation={{
          nameTooLong: (name: string) =>
            name.length < 256 ||
            t`Folder names cannot be longer than 255 characters. Please retry with a shorter name.`,
        }}
      />
      <ModalHeader bgColor={headerBg} borderTopRadius="md" fontSize="md">
        <HStack w="100%">
          {parentFolderId && (
            <IconButton
              size="sm"
              icon={<NavigateBackIcon />}
              aria-label={t`Move up`}
              variant="ghost"
              onClick={(_) => setCurrentFolderId(parentFolderId)}
            />
          )}
          <span>{name}</span>
        </HStack>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody p={0}>
        <Stack w="100%" divider={<StackDivider />} spacing={0}>
          <StackDivider />
          {orderedSubFolders.length &&
            orderedSubFolders.map((subFolder) => {
              if (!subFolder || !subFolder.id) {
                return null;
              }
              const subFolderId = subFolder.id;
              const subFolderIsBeingMoved = !!nodesBeingMovedIds.find(
                (moveId) => moveId === subFolderId,
              );
              return (
                <MoveButton
                  onClick={() => setCurrentFolderId(subFolderId)}
                  isDisabled={
                    subFolderIsBeingMoved || !subFolder.canMoveInto?.value
                  }
                  key={subFolderId}
                  leftIcon={<FolderOpenIcon />}
                >
                  {subFolder.name} {subFolderIsBeingMoved && t`(moving...)`}
                </MoveButton>
              );
            })}
          <MoveButton
            onClick={onOpenCreateFolderDialog}
            leftIcon={<CreateFolderIcon />}
            isDisabled={!folder.canCreateFolderIn?.value}
          >
            {t`Create folder...`}
          </MoveButton>
          <StackDivider />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Spacer />
        <Button
          size="sm"
          colorScheme="blue"
          onClick={onMove}
          isDisabled={!folder.canMoveInto?.value || isMoveWithinItself}
          isLoading={isMoveLoading}
        >
          {t`Move here`}
        </Button>
      </ModalFooter>
    </>
  );
};

const ErrorFallback = () => {
  return (
    <SimpleCard>
      <ErrorMessage size="lg" title={t`Error loading folders.`} />
    </SimpleCard>
  );
};

const MoveButton = (props: ButtonProps) => {
  return (
    <Button
      borderRadius={0}
      variant="ghost"
      colorScheme="blue"
      justifyContent="start"
      {...props}
    />
  );
};
